import React, { useState, useEffect } from "react";
import api from "../../api/axios.js";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategories } from "../../features/categories/categoriesSlice.js";
import "./AddProductForm.css";

function AddProductForm() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get categories from Redux store
  const { items: categories = [] } = useSelector((state) => state.categories);

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Initial form state
  const initialFormState = {
    name_en: "",
    name_ar: "",
    description_en: "",
    description_ar: "",
    price: "",
    price_after_discount: "",
    best_selling: 1,
    stock: "",
    category_id: "",
    image: null,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });

    // Clear error when field changes
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  // Validate form before submitting
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name_en.trim()) newErrors.name_en = t("errorsf.required");
    if (!formData.name_ar.trim()) newErrors.name_ar = t("errorsf.required");
    if (!formData.description_en.trim())
      newErrors.description_en = t("errorsf.required");
    if (!formData.description_ar.trim())
      newErrors.description_ar = t("errorsf.required");

    if (!formData.price) newErrors.price = t("errorsf.required");
    if (formData.price && isNaN(formData.price))
      newErrors.price = t("errorsf.number");

    if (!formData.price_after_discount)
      newErrors.price_after_discount = t("errorsf.required");

    if (!formData.stock) newErrors.stock = t("errorsf.required");

    if (!formData.category_id) newErrors.category_id = t("errorsf.required");

    if (!formData.image) newErrors.image = t("errorsf.required");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Sweetalert helpers
  const showErrorAlert = (message) => {
    Swal.fire({
      icon: "error",
      title: t("alerts.error"),
      text: message,
      confirmButtonColor: "#3085d6",
    });
  };

  const showSuccessAlert = () => {
    Swal.fire({
      icon: "success",
      title: t("alerts.success"),
      text: t("alerts.product_added"),
      confirmButtonColor: "#3085d6",
    });
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showErrorAlert(t("alerts.fix_errors"));
      return;
    }

    setIsLoading(true);
    Swal.fire({
      title: t("alerts.processing"),
      html: t("alerts.please_wait"),
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    const data = new FormData();
    for (const key in formData) {
      if (formData[key] !== null && formData[key] !== "")
        data.append(key, formData[key]);
    }

    try {
      await api.post("/products", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.close();
      showSuccessAlert();
      setFormData(initialFormState);
      navigate("/products");
    } catch (err) {
      Swal.close();
      const errorMessage =
        err.response?.data?.message || err.message || t("alerts.failed_add");
      showErrorAlert(errorMessage);
      if (err.response?.data?.errors) setErrors(err.response.data.errors);
    } finally {
      setIsLoading(false);
    }
  };

 return (
   <div
     className="py-4 px-2 px-md-3"
     dir={i18n.dir()}
     style={{
       minHeight: "100vh",
       backgroundColor: "#FDFBFC",
     }}
   >
     {/* Page Header */}
     <div className="mb-4">
       <div className="d-flex align-items-center gap-3">
         <div
           className="d-flex align-items-center justify-content-center rounded-3"
           style={{
             width: "48px",
             height: "48px",
             backgroundColor: "#F9E9EE",
             color: "#B5183F",
             flexShrink: 0,
           }}
         >
           <i className="fa-solid fa-box-open fs-5"></i>
         </div>

         <div>
           <h2
             className="fw-bold mb-1"
             style={{
               color: "#272727",
               fontSize: "clamp(1.4rem, 3vw, 2rem)",
             }}
           >
             {t("add_product.title")}
           </h2>

           <p
             className="mb-0"
             style={{
               color: "#999",
               fontSize: "13px",
             }}
           >
             {i18n.language === "ar"
               ? "أضف منتجًا جديدًا إلى المتجر"
               : "Add a new product to your store"}
           </p>
         </div>
       </div>
     </div>

     {/* Main Card */}
     <div
       className="rounded-4 p-3 p-md-4"
       style={{
         backgroundColor: "#fff",
         border: "1px solid #EEE5E8",
         boxShadow: "0 10px 35px rgba(39,39,39,.06)",
       }}
     >
       <form onSubmit={handleSubmit}>
         <div className="row g-4">
           {/* Product Information */}
           <div className="col-12">
             <div
               className="d-flex align-items-center gap-2 pb-3 mb-1"
               style={{
                 borderBottom: "1px solid #F0E8EB",
               }}
             >
               <div
                 className="d-flex align-items-center justify-content-center rounded-3"
                 style={{
                   width: "38px",
                   height: "38px",
                   backgroundColor: "#F9E9EE",
                   color: "#B5183F",
                 }}
               >
                 <i className="fa-solid fa-circle-info"></i>
               </div>

               <div>
                 <h5 className="fw-bold mb-0" style={{ color: "#272727" }}>
                   {i18n.language === "ar"
                     ? "بيانات المنتج"
                     : "Product Information"}
                 </h5>

                 <small style={{ color: "#999" }}>
                   {i18n.language === "ar"
                     ? "أدخل بيانات المنتج الأساسية"
                     : "Enter the basic product information"}
                 </small>
               </div>
             </div>
           </div>

           {/* English Name */}
           <div className="col-12 col-md-6">
             <label
               className="form-label fw-semibold"
               style={{
                 color: "#555",
                 fontSize: "13px",
               }}
             >
               {t("add_product.name_en")} *
             </label>

             <input
               type="text"
               className={`form-control rounded-3 ${
                 errors.name_en ? "is-invalid" : ""
               }`}
               name="name_en"
               value={formData.name_en}
               onChange={handleChange}
               placeholder={t("add_product.name_en")}
               style={{
                 minHeight: "48px",
                 border: "1px solid #E5E5E5",
                 boxShadow: "none",
               }}
             />

             {errors.name_en && (
               <div className="invalid-feedback">{errors.name_en}</div>
             )}
           </div>

           {/* Arabic Name */}
           <div className="col-12 col-md-6">
             <label
               className="form-label fw-semibold"
               style={{
                 color: "#555",
                 fontSize: "13px",
               }}
             >
               {t("add_product.name_ar")} *
             </label>

             <input
               type="text"
               className={`form-control rounded-3 ${
                 errors.name_ar ? "is-invalid" : ""
               }`}
               name="name_ar"
               value={formData.name_ar}
               onChange={handleChange}
               placeholder={t("add_product.name_ar")}
               style={{
                 minHeight: "48px",
                 border: "1px solid #E5E5E5",
                 boxShadow: "none",
               }}
             />

             {errors.name_ar && (
               <div className="invalid-feedback">{errors.name_ar}</div>
             )}
           </div>

           {/* English Description */}
           <div className="col-12 col-md-6">
             <label
               className="form-label fw-semibold"
               style={{
                 color: "#555",
                 fontSize: "13px",
               }}
             >
               {t("add_product.description_en")} *
             </label>

             <textarea
               className={`form-control rounded-3 ${
                 errors.description_en ? "is-invalid" : ""
               }`}
               name="description_en"
               value={formData.description_en}
               onChange={handleChange}
               placeholder={t("add_product.description_en")}
               rows={5}
               style={{
                 border: "1px solid #E5E5E5",
                 boxShadow: "none",
                 resize: "vertical",
               }}
             />

             {errors.description_en && (
               <div className="invalid-feedback">{errors.description_en}</div>
             )}
           </div>

           {/* Arabic Description */}
           <div className="col-12 col-md-6">
             <label
               className="form-label fw-semibold"
               style={{
                 color: "#555",
                 fontSize: "13px",
               }}
             >
               {t("add_product.description_ar")} *
             </label>

             <textarea
               className={`form-control rounded-3 ${
                 errors.description_ar ? "is-invalid" : ""
               }`}
               name="description_ar"
               value={formData.description_ar}
               onChange={handleChange}
               placeholder={t("add_product.description_ar")}
               rows={5}
               style={{
                 border: "1px solid #E5E5E5",
                 boxShadow: "none",
                 resize: "vertical",
               }}
             />

             {errors.description_ar && (
               <div className="invalid-feedback">{errors.description_ar}</div>
             )}
           </div>

           {/* Divider */}
           <div className="col-12">
             <div
               style={{
                 borderTop: "1px dashed #E5E5E5",
               }}
             />
           </div>

           {/* Pricing Section */}
           <div className="col-12">
             <div className="d-flex align-items-center gap-2">
               <div
                 className="d-flex align-items-center justify-content-center rounded-3"
                 style={{
                   width: "38px",
                   height: "38px",
                   backgroundColor: "#DEEDE8",
                   color: "#2F7662",
                 }}
               >
                 <i className="fa-solid fa-tag"></i>
               </div>

               <div>
                 <h5 className="fw-bold mb-0" style={{ color: "#272727" }}>
                   {i18n.language === "ar"
                     ? "السعر والمخزون"
                     : "Pricing & Inventory"}
                 </h5>

                 <small style={{ color: "#999" }}>
                   {i18n.language === "ar"
                     ? "حدد السعر والكميات المتاحة"
                     : "Set pricing and available stock"}
                 </small>
               </div>
             </div>
           </div>

           {/* Price */}
           <div className="col-12 col-sm-6 col-lg-3">
             <label
               className="form-label fw-semibold"
               style={{
                 color: "#555",
                 fontSize: "13px",
               }}
             >
               {t("add_product.price")} *
             </label>

             <div className="position-relative">
               <input
                 type="number"
                 className={`form-control rounded-3 ${
                   errors.price ? "is-invalid" : ""
                 }`}
                 name="price"
                 value={formData.price}
                 onChange={handleChange}
                 min="0"
                 step="0.01"
                 placeholder="0.00"
                 style={{
                   minHeight: "48px",
                   border: "1px solid #E5E5E5",
                   boxShadow: "none",
                 }}
               />
             </div>

             {errors.price && (
               <div className="invalid-feedback d-block">{errors.price}</div>
             )}
           </div>

           {/* Discount Price */}
           <div className="col-12 col-sm-6 col-lg-3">
             <label
               className="form-label fw-semibold"
               style={{
                 color: "#555",
                 fontSize: "13px",
               }}
             >
               {t("add_product.price_after_discount")} *
             </label>

             <input
               type="number"
               className={`form-control rounded-3 ${
                 errors.price_after_discount ? "is-invalid" : ""
               }`}
               name="price_after_discount"
               value={formData.price_after_discount}
               onChange={handleChange}
               min="0"
               step="0.01"
               placeholder="0.00"
               style={{
                 minHeight: "48px",
                 border: "1px solid #E5E5E5",
                 boxShadow: "none",
               }}
             />

             {errors.price_after_discount && (
               <div className="invalid-feedback d-block">
                 {errors.price_after_discount}
               </div>
             )}
           </div>

           {/* Stock */}
           <div className="col-12 col-sm-6 col-lg-3">
             <label
               className="form-label fw-semibold"
               style={{
                 color: "#555",
                 fontSize: "13px",
               }}
             >
               {t("add_product.stock")} *
             </label>

             <input
               type="number"
               className={`form-control rounded-3 ${
                 errors.stock ? "is-invalid" : ""
               }`}
               name="stock"
               value={formData.stock}
               onChange={handleChange}
               min="0"
               placeholder="0"
               style={{
                 minHeight: "48px",
                 border: "1px solid #E5E5E5",
                 boxShadow: "none",
               }}
             />

             {errors.stock && (
               <div className="invalid-feedback d-block">{errors.stock}</div>
             )}
           </div>

           {/* Best Selling */}
           <div className="col-12 col-sm-6 col-lg-3">
             <label
               className="form-label fw-semibold"
               style={{
                 color: "#555",
                 fontSize: "13px",
               }}
             >
               {t("add_product.best_selling")} *
             </label>

             <input
               type="number"
               className={`form-control rounded-3 ${
                 errors.best_selling ? "is-invalid" : ""
               }`}
               name="best_selling"
               value={formData.best_selling}
               onChange={handleChange}
               min="0"
               style={{
                 minHeight: "48px",
                 border: "1px solid #E5E5E5",
                 boxShadow: "none",
               }}
             />

             {errors.best_selling && (
               <div className="invalid-feedback d-block">
                 {errors.best_selling}
               </div>
             )}
           </div>

           {/* Category */}
           <div className="col-12 col-md-6">
             <label
               className="form-label fw-semibold"
               style={{
                 color: "#555",
                 fontSize: "13px",
               }}
             >
               {t("add_product.category")} *
             </label>

             <select
               className={`form-select rounded-3 ${
                 errors.category_id ? "is-invalid" : ""
               }`}
               name="category_id"
               value={formData.category_id}
               onChange={handleChange}
               style={{
                 minHeight: "48px",
                 border: "1px solid #E5E5E5",
                 boxShadow: "none",
                 cursor: "pointer",
               }}
             >
               <option value="">{t("add_product.select_category")}</option>

               {categories.map((cat) => (
                 <option key={cat.id} value={cat.id}>
                   {i18n.language === "ar" ? cat.name_ar : cat.name_en}
                 </option>
               ))}
             </select>

             {errors.category_id && (
               <div className="invalid-feedback">{errors.category_id}</div>
             )}
           </div>

           {/* Image */}
           <div className="col-12 col-md-6">
             <label
               className="form-label fw-semibold"
               style={{
                 color: "#555",
                 fontSize: "13px",
               }}
             >
               {t("add_product.image")} *
             </label>

             <div
               className="rounded-3 p-3"
               style={{
                 backgroundColor: "#FDFBFC",
                 border: `1px dashed ${errors.image ? "#dc3545" : "#DCCED3"}`,
               }}
             >
               <input
                 type="file"
                 className={`form-control ${errors.image ? "is-invalid" : ""}`}
                 name="image"
                 onChange={handleChange}
                 accept="image/*"
                 style={{
                   border: "none",
                   backgroundColor: "transparent",
                   boxShadow: "none",
                 }}
               />

               <small
                 className="d-block mt-2"
                 style={{
                   color: "#999",
                   fontSize: "11px",
                 }}
               >
                 {i18n.language === "ar"
                   ? "اختر صورة واضحة للمنتج"
                   : "Choose a clear product image"}
               </small>
             </div>

             {errors.image && (
               <div className="invalid-feedback d-block">{errors.image}</div>
             )}
           </div>

           {/* Image Preview */}
           {formData.image && (
             <div className="col-12">
               <div
                 className="rounded-4 p-3"
                 style={{
                   backgroundColor: "#FDFBFC",
                   border: "1px solid #EEE5E8",
                 }}
               >
                 <div className="d-flex align-items-center gap-2 mb-3">
                   <i
                     className="fa-solid fa-image"
                     style={{ color: "#B5183F" }}
                   />

                   <span
                     className="fw-semibold"
                     style={{
                       color: "#555",
                       fontSize: "13px",
                     }}
                   >
                     {i18n.language === "ar"
                       ? "معاينة الصورة"
                       : "Image Preview"}
                   </span>
                 </div>

                 <div
                   className="d-flex justify-content-center align-items-center rounded-3"
                   style={{
                     minHeight: "180px",
                     backgroundColor: "#fff",
                     border: "1px solid #EEE5E8",
                     overflow: "hidden",
                   }}
                 >
                   <img
                     src={URL.createObjectURL(formData.image)}
                     alt="Preview"
                     className="img-fluid"
                     style={{
                       maxHeight: "220px",
                       maxWidth: "100%",
                       objectFit: "contain",
                     }}
                   />
                 </div>
               </div>
             </div>
           )}

           {/* Actions */}
           <div className="col-12">
             <div
               className="d-flex flex-column flex-sm-row justify-content-end gap-2 pt-3"
               style={{
                 borderTop: "1px solid #F0E8EB",
               }}
             >
               <button
                 type="button"
                 onClick={() => navigate("/products")}
                 className="btn rounded-3 px-4 py-2 fw-semibold"
                 disabled={isLoading}
                 style={{
                   minHeight: "46px",
                   backgroundColor: "#F5F5F5",
                   color: "#666",
                   border: "1px solid #E5E5E5",
                 }}
               >
                 {i18n.language === "ar" ? "إلغاء" : "Cancel"}
               </button>

               <button
                 type="submit"
                 className="btn text-white rounded-3 px-4 py-2 fw-semibold"
                 disabled={isLoading}
                 style={{
                   minHeight: "46px",
                   backgroundColor: "#B5183F",
                   border: "none",
                   boxShadow: "0 8px 20px rgba(181,24,63,.16)",
                 }}
               >
                 {isLoading ? (
                   <>
                     <span
                       className="spinner-border spinner-border-sm me-2"
                       role="status"
                     />

                     {t("add_product.adding")}
                   </>
                 ) : (
                   <>
                     <i className="fa-solid fa-plus me-2"></i>
                     {t("add_product.add")}
                   </>
                 )}
               </button>
             </div>
           </div>
         </div>
       </form>
     </div>
   </div>
 );
}

export default AddProductForm;
